import { fetchShipmentProducts } from '@/apis/shipment';
import {
  ErrorLike,
  ProductDetailColumns,
  ShipmentProductList,
} from '@/apis/types';
import { CopyOUtlined } from '@/assets/icons/icons';
import ChevronRight from '@/assets/images/ChevronRight';
import { useToken } from '@/common/helper';
import { CustomButton, CustomTitle } from '@/components';
import { useSpin } from '@/components/SpinContext';
import useUnauthorize from '@/hooks/useUnauthorize';
import { Flex, message } from 'antd';
import React from 'react';
import ShipmentDetailProductsTable, {
  getProductDetailColumns,
} from './ShipmentDetailProductsTable';

interface Props {
  products: ShipmentProductList[];
  detailColumns?: ProductDetailColumns;
  shipmentId?: string;
  productsCount?: number;
  legType?: 'CL' | 'EL';
  onExpand?: (
    action?: string | boolean,
    type?: string,
    id?: string,
    alert?: unknown
  ) => void;
}
const ProductsTableSection: React.FC<Props> = ({
  products,
  detailColumns: detailColumnsProp,
  shipmentId,
  productsCount,
  onExpand,
  legType,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const data_length = products && products.length > 0;

  const handleCopyTable = async () => {
    showSpin();

    try {
      const model = {
        ...(legType === 'EL'
          ? { execution_leg_id: shipmentId }
          : { shipment_id: shipmentId }),
        search: '',
        sort_key: '',
        sort_order: '',
        page: 0,
        page_size: 0,
      };
      const res = await fetchShipmentProducts(token ?? '', model, legType);

      const data = res?.data?.data;

      if (!data?.is_success) {
        message.error(res?.data?.data?.message);
        return;
      }

      const products = (data?.data?.products ?? []) as ShipmentProductList[];

      if (!products?.length) {
        message.warning('No products to copy');
        return;
      }

      const detailColumns =
        data?.data?.product_detail_columns !== undefined
          ? data.data.product_detail_columns
          : getProductDetailColumns(products);
      const joinLots = (
        p: ShipmentProductList,
        key: 'lot_number' | 'expiration_date'
      ) => p?.lots?.map((lot) => lot[key] || '--').join(', ') || '--';

      const header = [
        'Product',
        'Qty',
        ...(detailColumns === 'serials' ? ['Serials'] : []),
        ...(detailColumns === 'lots' ? ['Lot #', 'Expiration date'] : []),
      ].join('\t');

      const rows = products?.map((p: ShipmentProductList) =>
        [
          p?.name ?? '--',
          p?.quantity ?? '--',
          ...(detailColumns === 'serials'
            ? [p?.serial_preview?.length ? p.serial_preview.join(', ') : '--']
            : []),
          ...(detailColumns === 'lots'
            ? [joinLots(p, 'lot_number'), joinLots(p, 'expiration_date')]
            : []),
        ].join('\t')
      );

      await navigator.clipboard.writeText([header, ...rows].join('\n'));

      message.success('Copied table');
    } catch (error) {
      unauthorize(error as ErrorLike);
    } finally {
      hideSpin();
    }
  };

  return (
    <Flex vertical gap={14}>
      {/* Header */}
      <Flex justify='space-between' align='center'>
        <CustomTitle
          text={`Products (${productsCount ?? 0})`}
          fontSize={18}
          fontWeight={700}
          lineHeight='24px'
          color='var(--text-primary)'
          marginBottom='0'
        />
        {data_length && (
          <Flex gap={8} align='center'>
            <CustomButton
              onClick={handleCopyTable}
              text='Copy'
              height='28px'
              fontSize='12px'
              lineHeight='16px'
              fontWeight={500}
              borderRadius='8px'
              bgColor='var(--light-gray)'
              color='var(--text-primary)'
              boxShadow='none'
              iconPosition='left'
              icon={<CopyOUtlined />}
              gap='10px'
            />
            <ChevronRight
              className='cursor-pointer'
              onClick={() => onExpand?.('open', 'shipmentProductsList')}
            />
          </Flex>
        )}
      </Flex>
      {/* Table */}
      {data_length && (
        <ShipmentDetailProductsTable
          products={products}
          previewCount={3}
          detailColumns={detailColumnsProp}
        />
      )}
    </Flex>
  );
};

export default ProductsTableSection;
